import { Component, ElementRef, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@fb-tool-front/environments/environment';
import { Observable } from 'rxjs';
import { TUI_ARROW } from '@taiga-ui/kit';
import {
  AppStateService,
  HelpersService,
  MetricsService,
  TRACKABLE_EVENTS
} from '@fb-tool-front/core/services';
import { cookiePrefix } from '@fb-tool-front/shared/constants/shared.constant';
import { CookieService } from 'ngx-cookie-service';
import dayjs from 'dayjs';
import { LOCATION } from '@ng-web-apis/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TourService } from '@fb-tool-front/core/services/tour.service';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CustomDialogService } from '@fb-tool-front/shared/components/dialog/dialog.component';
import { MobileModalComponent } from '@fb-tool-front/shared/components/mobile-modal/mobile-modal.component';
import { TUI_IS_MOBILE } from '@taiga-ui/cdk';
import { DOCUMENT } from '@angular/common';
import { Client, PlatformUrlName, User } from '@fb-tool-front/shared/interfaces/auth.type';
import { AuthService } from '@fb-tool-front/core/services/auth.service';
import { LocaleService } from '@fb-tool-front/core/services/locale.service';
import { TranslateService } from '@ngx-translate/core';
import { map, skip, switchMap } from 'rxjs/operators';
import { PricingModalComponent } from '@fb-tool-front/shared/components/pricing-modal/pricing-modal.component';
import { StripeStore } from '@fb-tool-front/stripe/store/stripe.store';
import { Stripe } from '@fb-tool-front/stripe/models/stripe.model';

export interface UrlList {
  name: string;
  routerLink: string;
  description: string;
  comingSoon: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss']
})
export class ToolbarComponent implements OnInit, OnDestroy {
  readonly pagesToState = {
    overlays: false,
    videos: true,
    statistics: true
  };
  readonly arrow = TUI_ARROW;
  readonly toolsDropdownData = [
    {
      name: 'TikTok',
      icon: 'tiktok',
      command: this.goToTool.bind(this, 'tiktok')
    },
    {
      name: 'Google',
      icon: 'google',
      command: this.goToTool.bind(this, 'google')
    }
  ];
  readonly userDropdownData = [
    {
      name: this.translateService.stream('switch client'),
      icon: 'tuiIconUsers',
      filterable: true,
      command: () => this.authService.changeClient()
    },
    {
      name: this.translateService.stream('settings'),
      icon: 'settings',
      command: () => {
        this.metricsService.trackEvent(TRACKABLE_EVENTS.openSettingsPage());
        this.router.navigate(['settings'], {
          state: {
            adAccounts: this.pagesToState[this.router.url.split('/').filter(Boolean)[0]],
            redirect: this.router.url
          }
        });
        this.userDropdownOpenStatus = false;
      }
    },
    // {
    //   name: this.translateService.stream('delete account'),
    //   icon: 'trash',
    //   command: () => {
    //     this.metricsService.trackEvent(TRACKABLE_EVENTS.deleteAccount());
    //     this.customDialogService
    //       .open(new PolymorpheusComponent(ConnectFacebookModalComponent, this.injector), {
    //         heading: `😔 ${this.translateService.instant('Delete account')}`,
    //         customButtons: true
    //       })
    //       .pipe(
    //         take(1),
    //         filter(res => !!res),
    //         switchMap(() => this.authService.deleteAccount())
    //       )
    //       .subscribe(() => this.authService.logout());
    //
    //     this.userDropdownOpenStatus = false;
    //   }
    // },
    {
      name: this.translateService.stream('logout'),
      icon: 'logout',
      command: () => this.authService.logout()
    }
  ];

  userDropdownOpenStatus = false;
  isNightTheme$: Observable<boolean>;
  user$: Observable<User>;
  client$: Observable<Client>;
  clientsList$: Observable<Client[]>;
  isSidebarOpen = false;

  themeToggleLabel$: Observable<string>;
  isLangChangeShown$: Observable<boolean>;
  isPricingShown$ = this.stripeStore.currentProduct$.pipe(
    skip(1),
    map((currentProduct: Stripe.Product) =>
      currentProduct ? typeof currentProduct === 'object' : true
    )
  );

  isStatisticsShown$ = this.authService.isStatisticsShown$;

  toggleSidebar(open: boolean) {
    this.isSidebarOpen = open;
  }

  constructor(
    private metricsService: MetricsService,
    private router: Router,
    @Inject(LOCATION) readonly location: Location,
    private appStateService: AppStateService,
    private cookieService: CookieService,
    private tourService: TourService,
    private customDialogService: CustomDialogService,
    public authService: AuthService,
    public stripeStore: StripeStore,
    public elementRef: ElementRef,
    public helperService: HelpersService,
    public localeService: LocaleService,
    private translateService: TranslateService,
    @Inject(Injector) private readonly injector: Injector,
    @Inject(DOCUMENT) private readonly documentRef: Document,
    @Inject(TUI_IS_MOBILE) private readonly isMobile: boolean
  ) {}

  ngOnDestroy() {
    // workaround to show login page on mobile in acceptable style
    this.documentRef
      .querySelector('meta[name=viewport]')
      .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
  }

  ngOnInit() {
    // workaround to show login page on mobile in acceptable style

    if (this.isMobile) {
      this.customDialogService
        .open(new PolymorpheusComponent(MobileModalComponent), {
          closeable: false,
          customButtons: true,
          withoutHeading: true
        })
        .subscribe(() => {
          this.documentRef
            .querySelector('meta[name=viewport]')
            .setAttribute('content', 'width=device-width, initial-scale=0.3, maximum-scale=1');
        });
    }

    this.isNightTheme$ = this.appStateService.isNightTheme$;
    this.themeToggleLabel$ = this.appStateService.isNightTheme$.pipe(
      switchMap(res => {
        if (res) {
          return this.translateService.stream('dark theme');
        } else {
          return this.translateService.stream('light theme');
        }
      })
    );

    this.user$ = this.authService.user$;
    this.client$ = this.authService.client$;
    this.clientsList$ = this.authService.allClientsList$;
    this.isLangChangeShown$ = this.authService.user$.pipe(
      map((user: User) => user?.roles?.includes('ROLE_CHANGE_LANG'))
    );
  }

  onThemeChanged(value: boolean) {
    this.appStateService.isNightTheme$$.next(value);
    this.cookieService.set(
      `${cookiePrefix}is-night-theme`,
      String(value),
      dayjs().add(90, 'day').toDate(),
      '/',
      `.${environment.aitarget_domain}`
    );
  }

  onLangChanged() {
    this.localeService.switchLocale();
  }

  goToTool(value: PlatformUrlName) {
    const toolName = {
      fb: 'fb',
      tiktok: 'tt',
      google: 'yt'
    }[value];

    const url = environment.production
      ? `https://${environment.aitarget_subdomain}.${environment.aitarget_domain}/${toolName}${
          this.router.url
        }?clientId=${this.authService.getCurrentClient().id}`
      : `https://${environment.aitarget_subdomain}.${environment.aitarget_domain}/${toolName}${
          this.router.url
        }?clientId=${this.authService.getCurrentClient().id}`;

    this.location.href = url;
  }

  isHelpIconShown() {
    return this.location.pathname.includes('overlays');
  }

  startTour() {
    this.metricsService.trackEvent(TRACKABLE_EVENTS.overlaysHelpClicked());
    this.tourService.start();
  }

  isHelpIconDisabled() {
    return !this.tourService.isInitialized;
  }

  public onHomeClick() {
    this.metricsService.trackEvent(TRACKABLE_EVENTS.homeMenuItemClicked());
  }

  public onOverlaysClick() {
    this.metricsService.trackEvent(TRACKABLE_EVENTS.overlaysMenuItemClicked());
  }

  public onVideoClick() {
    this.metricsService.trackEvent(TRACKABLE_EVENTS.videoMenuItemClicked());
  }

  public openPricingModal() {
    this.customDialogService
      .open(new PolymorpheusComponent(PricingModalComponent, this.injector), {
        closeable: true,
        withoutHeading: true,
        customButtons: true
      })
      .pipe(untilDestroyed(this))
      .subscribe();
  }
}
